import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import theme from "../../theme";

// Timeline rendering rules follow the join-request audit-log spec:
// - action drives icon + accent colour
// - actor null → "Employee" (or the join request's own employee name if we have it)
// - UPDATE rows render a before/after diff of the employment_data snapshots
// - metadata.reason on REJECTED must be shown inline
// - metadata.bulk on APPROVED gets a "bulk approval" pill
// - newest first (server order is preserved)

const ACTION_META = {
  CREATE: {
    label: "Employee submitted registration",
    Icon: AddCircleOutlineIcon,
    accent: "#8892a4",
    bg: theme.colors.Porcelain,
  },
  UPDATE: {
    label: "Payload edited",
    Icon: EditOutlinedIcon,
    accent: "#8892a4",
    bg: theme.colors.Porcelain,
  },
  SUBMITTED: {
    label: "Submitted for review",
    Icon: SendOutlinedIcon,
    accent: theme.colors.DodgerBlue,
    bg: "#ECF3FF",
  },
  APPROVED: {
    label: "Approved",
    Icon: CheckCircleOutlineIcon,
    accent: "#1A8C4E",
    bg: "#E8FDF1",
  },
  REJECTED: {
    label: "Rejected",
    Icon: CancelOutlinedIcon,
    accent: theme.colors.Monza,
    bg: theme.colors.Cinderella,
  },
};

const useStyles = makeStyles(() => ({
  root: { padding: "4px 4px 16px", display: "flex", flexDirection: "column", gap: 6 },
  loading: { display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0" },
  emptyWrap: {
    padding: "32px 24px",
    textAlign: "center",
    borderRadius: 12,
    background: theme.colors.Porcelain,
    border: `1px dashed ${theme.colors.Gallery}`,
  },
  emptyTitle: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    fontSize: 13.5,
  },
  emptySubtitle: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    fontSize: 12,
    marginTop: 4,
  },
  entry: {
    display: "grid",
    gridTemplateColumns: "36px 1fr",
    gap: 12,
    padding: "10px 0",
    position: "relative",
    // Vertical thread between entries — anchors the icons visually.
    "&:not(:last-child)::before": {
      content: '""',
      position: "absolute",
      left: 17,
      top: 42,
      bottom: -4,
      width: 2,
      background: theme.colors.Zumthor,
    },
  },
  iconBubble: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    zIndex: 1,
  },
  entryBody: { minWidth: 0 },
  headerRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  actionLabel: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    fontSize: 13.5,
    lineHeight: 1.35,
  },
  bulkPill: {
    height: 20,
    fontSize: 10.5,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    background: "#E8FDF1",
    color: "#1A8C4E",
    letterSpacing: 0.2,
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
  },
  actorName: { color: theme.colors.Violet, fontFamily: theme.fontFamily.MARKPROBOLD },
  actorDivider: { color: "#C5DAFE" },
  reasonBox: {
    marginTop: 8,
    padding: "10px 12px",
    borderRadius: 10,
    background: theme.colors.Cinderella,
    border: `1px solid #F7C6CD`,
    color: theme.colors.Monza,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  reasonLabel: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginBottom: 2,
    display: "block",
  },
  diffBox: {
    marginTop: 8,
    borderRadius: 10,
    background: theme.colors.Porcelain,
    border: `1px solid ${theme.colors.Gallery}`,
    overflow: "hidden",
  },
  diffHeader: {
    padding: "8px 12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: theme.colors.DodgerBlue,
    borderBottom: `1px solid ${theme.colors.Gallery}`,
    background: theme.colors.White,
  },
  diffRow: {
    display: "grid",
    gridTemplateColumns: "minmax(140px, 40%) 1fr 1fr",
    gap: 12,
    padding: "8px 12px",
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    borderTop: `1px solid ${theme.colors.Gallery}`,
    "&:first-of-type": { borderTop: "none" },
  },
  diffKey: { color: theme.colors.Violet, fontFamily: theme.fontFamily.MARKPROBOLD },
  diffOld: { color: theme.colors.Monza, textDecoration: "line-through", wordBreak: "break-word" },
  diffNew: { color: "#1A8C4E", wordBreak: "break-word" },
  diffEmpty: {
    padding: "10px 12px",
    fontSize: 12,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOOK,
  },
}));

const flatten = (obj, prefix = "") => {
  const out = {};
  if (obj === null || obj === undefined) return out;
  if (typeof obj !== "object") {
    out[prefix || "value"] = obj;
    return out;
  }
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(out, flatten(value, path));
    } else {
      out[path] = value;
    }
  });
  return out;
};

// Walk before/after and surface only changed keys — mirrors the join-request
// audit-log spec (UPDATE rows have full snapshots but ops only care about
// what actually moved).
const diffSnapshots = (before, after) => {
  const b = flatten(before || {});
  const a = flatten(after || {});
  const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const rows = [];
  keys.forEach((k) => {
    const bv = b[k];
    const av = a[k];
    if (JSON.stringify(bv) !== JSON.stringify(av)) {
      rows.push({ key: k, before: bv, after: av });
    }
  });
  return rows;
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const formatAbsolute = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return formatAbsolute(iso).split(",")[0];
};

// Actor null on the wire = the public employee flow (self-registration / submit).
// The spec says render it as "Employee" or the employee's own name — we fall back
// to the join-request payload the caller passes in.
const resolveActorName = (entry, employeeName) => {
  if (entry?.actor) {
    const parts = [entry.actor.first_name, entry.actor.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return parts || entry.actor.email || "—";
  }
  return employeeName || "Employee";
};

export default function AuditLogTimeline({
  entries = [],
  loading = false,
  employeeName,
}) {
  const classes = useStyles();
  const list = useMemo(() => (Array.isArray(entries) ? entries : []), [entries]);

  if (loading && list.length === 0) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={28} style={{ color: theme.colors.DodgerBlue }} />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className={classes.emptyWrap}>
        <div className={classes.emptyTitle}>No audit entries yet</div>
        <div className={classes.emptySubtitle}>
          The timeline shows submissions, edits, and review decisions once they happen.
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {list.map((entry, idx) => {
        const meta = ACTION_META[entry.action] || {
          label: entry.action || "Event",
          Icon: EditOutlinedIcon,
          accent: "#8892a4",
          bg: theme.colors.Porcelain,
        };
        const Icon = meta.Icon;
        const actor = resolveActorName(entry, employeeName);
        const isUpdate = entry.action === "UPDATE";
        const diffRows = isUpdate ? diffSnapshots(entry.before_snapshot, entry.after_snapshot) : [];

        return (
          <div key={entry.id || idx} className={classes.entry}>
            <div className={classes.iconBubble} style={{ background: meta.bg, color: meta.accent }}>
              <Icon fontSize="small" style={{ color: meta.accent }} />
            </div>
            <div className={classes.entryBody}>
              <div className={classes.headerRow}>
                <Typography component="span" className={classes.actionLabel}>
                  {meta.label}
                </Typography>
                {entry.action === "APPROVED" && entry.metadata?.bulk ? (
                  <Chip size="small" label="Bulk approval" className={classes.bulkPill} />
                ) : null}
              </div>
              <div className={classes.metaRow}>
                <span className={classes.actorName}>{actor}</span>
                <span className={classes.actorDivider}>·</span>
                <Tooltip title={formatAbsolute(entry.created_at)}>
                  <span>{formatRelative(entry.created_at)}</span>
                </Tooltip>
              </div>

              {entry.action === "REJECTED" && entry.metadata?.reason ? (
                <div className={classes.reasonBox}>
                  <span className={classes.reasonLabel}>Reason sent to employee</span>
                  {entry.metadata.reason}
                </div>
              ) : null}

              {isUpdate ? (
                <div className={classes.diffBox}>
                  <div className={classes.diffHeader}>Changes</div>
                  {diffRows.length === 0 ? (
                    <div className={classes.diffEmpty}>No field-level changes recorded.</div>
                  ) : (
                    diffRows.map((row) => (
                      <div className={classes.diffRow} key={row.key}>
                        <span className={classes.diffKey}>{row.key}</span>
                        <span className={classes.diffOld}>{formatVal(row.before)}</span>
                        <span className={classes.diffNew}>{formatVal(row.after)}</span>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
